import { ConditionalLink } from "components/conditionalLink";
import * as Accordion from "@radix-ui/react-accordion";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { Button } from "components/DesignSystem/Button/Button";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import Modal from "components/DesignSystem/Modal/Modal";
import RadioGroup from "components/DesignSystem/RadioGroup/RadioGroup";
import { MigrateToDirectConnection } from "components/Illustrations/MigrateToDirectConnection";
import { YYYY_MM_DD } from "constants/date";
import dayjs from "dayjs";
import { Formik, Form, Field, FieldProps } from "formik";
import { dataSourceDirectMigration } from "formValidations/dataSourceDirectMigration";
import { useBankConnect } from "hooks/useBankConnect";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useCurrentEntity } from "hooks/useCurrentEntity";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useGetAllConnectionsQuery } from "store/apis/booksConnections";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";
import { TRANSACTION_PULL_TYPE } from "types/contants/transactionPullTypes";
import { ArrowDown } from "components/icons/ArrowDown";
import { CaretDown } from "components/icons/CaretDown";

export const PlaidToDirectMigration = ({
  onClose,
  connectionId,
  entityId,
}: {
  connectionId: string;
  onClose: () => void;
  entityId: string;
}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entity = useCurrentEntity();
  const { isCpa, isForeignCA } = useRoleBasedView();
  const { link } = useConstructInternalLink();

  const { data: connections } = useGetAllConnectionsQuery(
    {
      groupId: groupId!,
      entityId: entityId!,
    },
    { skip: !groupId || !entityId }
  );
  const currentConnection = connections?.find(
    (connection) => connection.uuid === connectionId
  );

  const { onConnect, isLoading } = useBankConnect();

  const onSubmit = async ({
    replaceType,
    customDate,
  }: {
    replaceType: TRANSACTION_PULL_TYPE;
    customDate: string;
  }) => {
    try {
      await onConnect({
        connectionId,
        state: {
          txn_pull_choice: replaceType,
          txn_pull_date: customDate,
        },
        invokedFrom:
          isCpa || isForeignCA
            ? `/books/data-sources?company=${groupId}&entity=${entityId}`
            : `/books/data-sources?entity=${entityId}`,
      });

      onClose();
    } catch (error) {}
  };

  const { data: bookkeepingSettings } = useGetBookkeepingSettingsQuery(
    {
      entityId: entityId!,
    },
    { skip: !groupId || !entityId }
  );

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={{
        replaceType: "NA",
        customDate: "",
      }}
      validationSchema={dataSourceDirectMigration}
    >
      {({ values, submitForm, setFieldValue, setFieldTouched, errors }) => (
        <Form>
          <Modal.Header className="sm:t-pl-4">
            <Modal.Title>
              Connect {currentConnection?.name} Directly
            </Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body className="t-flex t-flex-col t-gap-4 sm:t-px-4">
            <MigrateToDirectConnection />

            <div className="t-text-body-sm">
              <p className="t-mb-3 t-text-subtitle-sm">
                Do you also want to replace your past transactions?
              </p>

              <RadioGroup.Root
                value={values.replaceType}
                onValueChange={(v) => {
                  setFieldValue("replaceType", v);
                  setFieldTouched("replaceType", true);
                }}
              >
                <div className="t-flex t-flex-col t-gap-2">
                  <RadioGroup.Item value="NA">No</RadioGroup.Item>
                  <RadioGroup.Item value="REPLACE_UNCATEGORIZED">
                    Yes, replace only uncategorised transactions
                  </RadioGroup.Item>

                  <Accordion.Root type="multiple">
                    <Accordion.Item value="advanced-options">
                      <Accordion.Content className="t-flex t-flex-col t-gap-2">
                        <RadioGroup.Item value="REPLACE_ALL">
                          Yes, replace all transactions
                        </RadioGroup.Item>
                        <RadioGroup.Item value="CUSTOM">
                          Yes, replace all transactions since a custom date
                        </RadioGroup.Item>
                        {(values.replaceType === "CUSTOM" ||
                          values.replaceType === "REPLACE_ALL") && (
                          <div className="t-mt-2">
                            <Banner
                              type="warning"
                              title="Note"
                              description={
                                <span>
                                  We recommend speaking with our support team
                                  before taking this action.
                                </span>
                              }
                              cta={
                                <ConditionalLink
                                  to={`/chat/${entity.bookkeeping_channel}`}
                                  className="!t-no-underline"
                                >
                                  <Button size="small">Go to chat</Button>
                                </ConditionalLink>
                              }
                            />
                          </div>
                        )}
                      </Accordion.Content>
                      <Accordion.Trigger className="all:unset t-flex t-gap-1 t-mt-3 t-group">
                        <div className="t-flex t-gap-1 t-items-center t-text-text-30 t-text-body-sm">
                          <span>
                            <span className="group-data-state-closed:t-hidden">
                              Hide
                            </span>{" "}
                            <span className="group-data-state-open:t-hidden">
                              View
                            </span>{" "}
                            advanced options
                          </span>
                          <span className="group-data-state-open:t-rotate-180 t-transition-transform t-flex">
                            <CaretDown className="t-h-3 t-w-3" />
                          </span>
                        </div>
                      </Accordion.Trigger>
                    </Accordion.Item>
                  </Accordion.Root>
                </div>
              </RadioGroup.Root>
            </div>

            {values.replaceType === "CUSTOM" && (
              <Field name="customDate">
                {({ field }: FieldProps) => {
                  return (
                    <DateInput
                      {...field}
                      portalId={field.name}
                      saveFormat={YYYY_MM_DD}
                      maxDate={new Date()}
                      label="Select custom date"
                    />
                  );
                }}
              </Field>
            )}

            {bookkeepingSettings?.accounting_lock_date && (
              <div className="t-mt-4">
                <Banner
                  type="neutral"
                  title={
                    <span>
                      Your financials are locked until{" "}
                      {dayjs(bookkeepingSettings.accounting_lock_date).format(
                        "MMM DD, YYYY"
                      )}
                    </span>
                  }
                  description={
                    <span>
                      To preserve closed financials, transactions before this
                      date won't be imported. If you need to include earlier
                      transactions, either update the lock date in{" "}
                      <ConditionalLink
                        to={link("/books/books-settings")}
                        className="t-text-text-30 !t-underline"
                      >
                        settings{" "}
                      </ConditionalLink>{" "}
                      or reach out to us via{" "}
                      <ConditionalLink
                        to={`/chat/${entity.bookkeeping_channel}`}
                        className="t-text-text-30 !t-underline"
                      >
                        chat
                      </ConditionalLink>{" "}
                      for assistance.
                    </span>
                  }
                />
              </div>
            )}
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Modal.RawClose asChild>
              <Button>Cancel</Button>
            </Modal.RawClose>
            <Button
              customType="primary"
              onClick={submitForm}
              isLoading={isLoading}
              disabled={isLoading}
            >
              Connect
            </Button>
          </Modal.FooterButtonGroup>
        </Form>
      )}
    </Formik>
  );
};
